import { useState, useCallback } from 'react';
import { Location } from '../types';

export default function useGeolocation() {
  const [location, setLocation] = useState<Location | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  const requestLocation = useCallback(() => {
    // Check if the browser supports geolocation API
    if (!navigator.geolocation) {
      console.warn('[Geolocation] Browser does not support geolocation API.');
      setLocationError('Geolocation is not supported by your browser.');
      return;
    }

    console.log('[Geolocation] Requesting location from browser...');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        console.log('[Geolocation] Permission granted. Coordinates received:', { latitude, longitude });

        // Set coordinates immediately before reverse geocoding completes
        setLocation({ latitude, longitude, name: null });

        // Reverse geocode coordinates to get a human-readable place name
        console.log('[Geolocation] Fetching place name from Nominatim...');
        fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
        )
          .then((res) => res.json())
          .then((data) => {
            // Pick the most specific available place name
            const name =
              data.address?.city ||
              data.address?.town ||
              data.address?.village ||
              data.address?.county ||
              data.display_name ||
              null;

            console.log('[Geolocation] Place name resolved:', name);
            setLocation({ latitude, longitude, name });
          })
          .catch((err) => {
            // Reverse geocoding failed but we still have coordinates — use them
            console.warn('[Geolocation] Nominatim reverse geocoding failed:', err.message);
            setLocation({ latitude, longitude, name: null });
          });
      },
      (err) => {
        // User denied permission or another browser error occurred
        console.error('[Geolocation] Permission denied or error:', err.message);
        setLocationError(err.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  return { location, locationError, requestLocation };
}
